// /onboarding/otp — the verification code (board 25 v2 erp/onboarding/otp--default,
// --wrong, --resend-cooldown, --locked; copy verbatim). Six cells, paste fills
// them, the sixth digit submits. Resend after 45 s (the business app's
// cooldown; Authentik gives no resend_after) re-posts the phone to the SAME
// flow; the countdown pauses while offline. A lockout (LOCKED:<s>) counts
// down LTR and re-enables the cells at zero. The server gives no attempts
// count, so the wrong-code line is the server's own sentence (rule 7) — except
// on an exchange channel (Asiacell SMS), whose real attempts_left is appended.
// The sub-line and the resend note name the channel that really delivered the
// code (the enrollment's readback; WhatsApp is the default when unknown). An
// expired code (410 from the exchange) clears the cells and offers the resend
// at once, without waiting out the countdown.

import { useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { useOnline } from '@/components/OfflineStrip';
import { isApiError } from '@/lib/api';
import { AuthError, AUTH_COPY } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { OTP } from '../copy';
import { failureOf } from '../data/api';
import { ONB } from '../data/flow';
import { clock, CodeCells, maskedPhone, Msg, useCountdown } from '../components/widgets';
import { Wizard, type WizardError } from '../components/Wizard';
import { onboardingStore } from '../state';

export const RESEND_SECONDS = 45;
export const CODE_LENGTH = 6;

type Status = 'default' | 'wrong' | 'incomplete' | 'expired' | 'resend-cooldown';
type SysError = { kind: 'network' | 'server'; ref: string | null; detail?: string | null; retry: () => void };

export function OtpScreen() {
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'phone';
  const online = useOnline();
  const enrollment = onboardingStore.enrollment();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>('default');
  const [message, setMessage] = useState<string | null>(null);
  const [sysError, setSysError] = useState<SysError | null>(null);
  const [resendLeft, restartResend] = useCountdown(RESEND_SECONDS, online);
  const [lockLeft, startLock] = useCountdown(0, true);
  const cellsRef = useRef<HTMLInputElement>(null);

  const channel = enrollment.channelInfo()?.channel ?? null;
  const stage = enrollment.stage();
  if (stage === 'pin') return <Navigate to={ONB.pin} replace />;
  if (stage !== 'otp') return <Navigate to={ONB.phone} replace />;

  const locked = lockLeft > 0;

  /** Shared failure mapping for verify and resend; `retry` re-runs the action on the error card. */
  const fail = (e: unknown, retry: () => void): void => {
    if (e instanceof AuthError) {
      if (e.code === 'locked' && e.retryAfterSeconds) {
        startLock(e.retryAfterSeconds);
        setCode('');
        setStatus('default');
        setMessage(null);
        return;
      }
      if (e.code === 'code_expired') {
        // The sent code is dead: nothing typed is worth keeping, and the resend must be reachable now.
        setCode('');
        setStatus('expired');
        setMessage(e.message || AUTH_COPY.codeExpired);
        restartResend(0);
        return;
      }
      if (e.code === 'flow_expired' || enrollment.stage() !== 'otp') {
        navigate(ONB.phone, { replace: true });
        return;
      }
      if (e.code === 'network') {
        setSysError({ kind: 'network', ref: null, retry });
        return;
      }
      setStatus('wrong');
      const left = e.code === 'invalid_code' ? e.attemptsLeft : undefined;
      const sentence = e.message || AUTH_COPY.unexpected;
      setMessage(left != null && left > 0 ? `${sentence} ${OTP.attemptsLeft(left)}` : sentence);
      return;
    }
    if (isApiError(e) && e.status > 0 && e.status < 500) {
      setStatus('wrong');
      setMessage(e.detail ?? e.message);
      return;
    }
    setSysError({ ...failureOf(e), retry });
  };

  const submit = async (value: string = code) => {
    if (busy || locked) return;
    setSysError(null);
    if (value.length !== CODE_LENGTH) {
      setStatus('incomplete');
      setMessage(OTP.incomplete);
      cellsRef.current?.focus();
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await enrollment.submitCode(value);
      navigate(ONB.pin);
    } catch (e) {
      // Wrong code: the cells shake and stay filled for correction (board otp--wrong).
      fail(e, () => void submit(value));
      cellsRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (busy || locked) return;
    setSysError(null);
    setMessage(null);
    try {
      await enrollment.resend();
      restartResend(RESEND_SECONDS);
      setCode('');
      setStatus('resend-cooldown');
      cellsRef.current?.focus();
    } catch (e) {
      fail(e, () => void resend());
    }
  };

  const changeNumber = () => {
    enrollment.reset();
    navigate(ONB.phone, { replace: true });
  };

  const error: WizardError | null = sysError
    ? {
        kind: sysError.kind,
        ref: sysError.ref,
        onRetry: () => {
          const again = sysError.retry;
          setSysError(null);
          again();
        },
      }
    : null;

  const state = locked ? 'locked' : busy ? 'busy' : status;

  return (
    <Wizard
      screen="otp"
      testId="onb-otp"
      state={state}
      question={OTP.title}
      sub={
        <>
          {channel === 'sms' ? OTP.subBeforeSms : channel === 'telegram' ? OTP.subBeforeTelegram : OTP.subBefore}
          <bdi dir="ltr" className="ltr" data-testid="onb-otp-phone">
            {maskedPhone(enrollment.phone)}
          </bdi>
          {OTP.subAfter}
        </>
      }
      onSubmit={() => void submit()}
      error={error}
      enterHint={!locked}
      peek={OTP.peek}
      panel={{ phase: 'identity', sub: 'otp' }}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={busy} disabled={locked} data-testid="onb-verify">
          {busy ? OTP.busy : OTP.verify}
        </PrimaryAction>
      }
    >
      <CodeCells
        ref={cellsRef}
        data-testid="onb-otp-input"
        label={OTP.title}
        autoFocus
        value={code}
        invalid={status === 'wrong'}
        disabled={locked}
        onChange={(v) => {
          setCode(v);
          if (status === 'wrong' || status === 'incomplete' || status === 'expired') {
            setStatus('default');
            setMessage(null);
          }
        }}
        onComplete={(v) => void submit(v)}
      />
      {(status === 'wrong' || status === 'incomplete' || status === 'expired') && message && !locked && <Msg testId="onb-otp-error">{message}</Msg>}
      {status === 'resend-cooldown' && !locked && (
        <Msg tone="ok" testId="onb-otp-note">
          {channel === 'sms' ? OTP.resentSms : channel === 'telegram' ? OTP.resentTelegram : OTP.resent}
        </Msg>
      )}
      {locked && (
        <Msg tone="warn" testId="onb-otp-locked">
          {OTP.locked}
          <bdi dir="ltr" className="ltr onb-clock">
            {clock(lockLeft)}
          </bdi>
          <br />
          {OTP.lockedSub}
        </Msg>
      )}
      <div className="onb-resend" data-testid="onb-resend">
        {locked ? (
          <span>{OTP.resendStopped}</span>
        ) : resendLeft > 0 ? (
          <span>
            {OTP.resendIn}
            <bdi dir="ltr" className="ltr onb-clock">
              {clock(resendLeft)}
            </bdi>
          </span>
        ) : (
          <button type="button" className="onb-tert" data-testid="onb-resend-btn" onClick={() => void resend()}>
            {OTP.resend}
          </button>
        )}
        <button type="button" className="onb-tert" data-testid="onb-change-number" onClick={changeNumber}>
          {OTP.change}
        </button>
      </div>
    </Wizard>
  );
}
